import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button, Text, useTheme } from "react-native-paper";
import type { MD3Theme } from "react-native-paper/lib/typescript/types";
import Box from "../Box";
import Container from "../Container";
import EmptyState from "../EmptyView";
import ScrollContainer from "../ScrollContainer";
import { LeaderboardBox } from "./LeaderboardBox";
import { LeaderboardList } from "./LeaderboardList";
import { LeaderboardListItemSkeleton } from "./LeaderboardListItemSkeleton";
import { LeaderboardRankCard } from "./LeaderboardRankCard";
import type {
	LeaderboardAvatarType,
	LeaderboardItem,
	LeaderboardScope,
} from "./types";

type Props = {
	items: LeaderboardItem[];
	isLoading?: boolean;
	currentUserItem?: LeaderboardItem;
	avatarType?: LeaderboardAvatarType;
	showUsersPodium?: boolean;
	numUsersToDisplay?: number;
	scope?: LeaderboardScope;
	error?: { title?: string; description?: string; ctaLabel?: string };
	emptyCopy?: { title?: string; description?: string; ctaLabel?: string };
	onStart?: () => void;
	onRefresh?: () => void;
	onScrolled?: (scrollY: number) => void;
};

const SKELETON_COUNT = 7;

type LoadedProps = {
	items: LeaderboardItem[];
	currentUserItem?: LeaderboardItem;
	avatarType?: LeaderboardAvatarType;
	showUsersPodium: boolean;
	numUsersToDisplay: number;
	onStart?: () => void;
	theme: MD3Theme;
	t: TFunction;
	onScrolled?: (scrollY: number) => void;
};

const LeaderboardContentLoaded = ({
	items,
	currentUserItem,
	avatarType,
	showUsersPodium,
	numUsersToDisplay,
	onStart,
	theme,
	t,
	onScrolled,
}: LoadedProps) => {
	const first = items.find((u) => u.rank === 1);
	const second = items.find((u) => u.rank === 2);
	const third = items.find((u) => u.rank === 3);
	const podiumItems = [second, first, third].filter((i) => !!i);

	const startRank = showUsersPodium ? 4 : 1;
	const rankedItems = items.filter(
		(i) => i.rank >= startRank && i.rank <= numUsersToDisplay,
	);
	const showRankings = rankedItems.length > 0;

	const rankingsTitle =
		rankedItems.length > 0
			? t("leaderboard.rankingsRange", {
					startRank,
					endRank: rankedItems[rankedItems.length - 1].rank,
				})
			: t("leaderboard.rankings");

	return (
		<ScrollContainer
			scrollEventThrottle={16}
			maxWidth="sm"
			onScroll={(e) => onScrolled?.(e.nativeEvent.contentOffset.y)}
		>
			<Box gap={16} paddingVertical={16}>
				{showUsersPodium && podiumItems.length > 0 && (
					<LeaderboardBox
						title={t("leaderboard.topPerformers")}
						enableGutters={false}
					>
						<Box
							flexDirection="row"
							gap={8}
							paddingHorizontal={12}
							paddingVertical={12}
						>
							{podiumItems.map((item) => (
								<Box key={item.rank} flex={item.rank === 1 ? 1.5 : 1}>
									<LeaderboardRankCard
										{...item}
										avatarType={avatarType}
										isCurrentUser={item.username === currentUserItem?.username}
									/>
								</Box>
							))}
						</Box>
					</LeaderboardBox>
				)}

				{!currentUserItem?.rank && (
					<LeaderboardBox title={t("leaderboard.yourPosition")}>
						<Box gap={16}>
							<Box flexDirection="row" gap={12}>
								<Box
									width={48}
									height={48}
									borderRadius={theme.roundness * 4}
									alignItems="center"
									justifyContent="center"
									style={{ backgroundColor: theme.colors.secondaryContainer }}
								>
									<Icon
										name="trending-up"
										size={24}
										color={theme.colors.primary}
									/>
								</Box>
								<Box flex={1} gap={4}>
									<Text variant="titleSmall">
										{t("leaderboard.unranked.title")}
									</Text>
									<Text
										variant="bodySmall"
										style={{ color: theme.colors.secondary }}
									>
										{t("leaderboard.unranked.description")}
									</Text>
								</Box>
							</Box>
							{onStart && (
								<Button mode="contained" onPress={onStart}>
									{t("leaderboard.unranked.cta")}
								</Button>
							)}
						</Box>
					</LeaderboardBox>
				)}

				{showRankings && (
					<LeaderboardBox title={rankingsTitle} enableGutters={false}>
						<LeaderboardList items={rankedItems} avatarType={avatarType} />
					</LeaderboardBox>
				)}
			</Box>
		</ScrollContainer>
	);
};

export const LeaderboardContent = ({
	items,
	isLoading = false,
	currentUserItem,
	avatarType,
	showUsersPodium = true,
	numUsersToDisplay = 10,
	scope,
	error,
	emptyCopy,
	onStart,
	onRefresh,
	onScrolled,
}: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();

	if (scope === "friends") {
		return (
			<EmptyState
				title={t("leaderboard.friends.comingSoonTitle")}
				subtitle={t("leaderboard.friends.comingSoonDescription")}
				cta={t("leaderboard.friends.comingSoonBadge")}
				image={(_props) => (
					<Box
						width={72}
						height={72}
						borderRadius={36}
						alignItems="center"
						justifyContent="center"
						style={{ backgroundColor: theme.colors.secondaryContainer }}
					>
						<Icon
							name="account-group-outline"
							size={32}
							color={theme.colors.primary}
						/>
					</Box>
				)}
			/>
		);
	}

	if (isLoading) {
		return (
			<Container maxWidth="sm">
				{Array.from({ length: SKELETON_COUNT }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton list
					<LeaderboardListItemSkeleton key={i} />
				))}
			</Container>
		);
	}

	if (error) {
		return (
			<EmptyState
				title={error.title ?? t("leaderboard.error.title")}
				subtitle={error.description ?? t("leaderboard.error.description")}
				cta={
					onRefresh
						? (error.ctaLabel ?? t("leaderboard.error.tryAgain"))
						: undefined
				}
				icon="refresh"
				image={(_props) => (
					<Box
						width={72}
						height={72}
						borderRadius={36}
						alignItems="center"
						justifyContent="center"
						style={{ backgroundColor: theme.colors.errorContainer }}
					>
						<Icon name="alert-outline" size={32} color={theme.colors.error} />
					</Box>
				)}
				handleAction={onRefresh}
			/>
		);
	}

	if (items.length < 3) {
		return (
			<EmptyState
				title={emptyCopy?.title ?? t("leaderboard.empty.title")}
				subtitle={emptyCopy?.description ?? t("leaderboard.empty.description")}
				cta={
					onStart
						? (emptyCopy?.ctaLabel ?? t("leaderboard.empty.startLearning"))
						: undefined
				}
				image={(_props) => (
					<Box
						width={72}
						height={72}
						borderRadius={36}
						alignItems="center"
						justifyContent="center"
						style={{ backgroundColor: theme.colors.secondaryContainer }}
					>
						<Icon
							name="trophy-outline"
							size={32}
							color={theme.colors.primary}
						/>
					</Box>
				)}
				handleAction={onStart}
			/>
		);
	}

	return (
		<LeaderboardContentLoaded
			items={items}
			currentUserItem={currentUserItem}
			avatarType={avatarType}
			showUsersPodium={showUsersPodium}
			numUsersToDisplay={numUsersToDisplay}
			onStart={onStart}
			theme={theme}
			t={t}
			onScrolled={onScrolled}
		/>
	);
};
